'use client'

/* Adapted from Beautiful UI (MIT): https://www.beautifului.dev/r/tool-chips.json
 * Run header plus expandable rows; the demo sequencing, diff chips and diff preview are dropped. */

import { useState, version } from 'react'
import { cn } from '../../lib/utils'
import { BUI_EASE } from './motion'

export interface ToolChip {
  /** Technical name shown as a mono chip; omit it to show the label alone. */
  name?: string
  label: string
  /** Lines shown when the row is expanded. */
  detail?: string[]
}

/** React 19 treats `inert` as a boolean; React 18 only forwards it as an empty-string attribute. */
const INERT = { inert: Number(version.split('.')[0]) >= 19 ? true : '' } as object

function Chevron({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden className={className} style={style}>
      <path d="M6 9l6 6 6-6" />
    </svg>
  )
}

function ToolRow({ tool, index }: { tool: ToolChip; index: number }) {
  const [open, setOpen] = useState(false)
  const hasDetail = !!tool.detail?.length
  return (
    <div className="bui-motion" style={{ animation: `bui-fade-up 300ms ${BUI_EASE} ${index * 70}ms both` }}>
      <button
        type="button"
        aria-expanded={hasDetail ? open : undefined}
        title={tool.name}
        onClick={() => hasDetail && setOpen((o) => !o)}
        className="group/row -mx-[3px] flex h-7 w-[calc(100%+6px)] min-w-0 items-center gap-2 rounded-[8px] px-[3px] text-left transition-colors duration-100 hover:bg-[var(--hover)] focus-visible:outline-2 focus-visible:outline-[var(--l3-violet)]"
      >
        <span className="relative flex size-4 shrink-0 items-center justify-center text-[var(--ink-3)]">
          <svg
            width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden
            className={cn('transition-opacity duration-100', hasDetail && 'group-hover/row:opacity-0', open && 'opacity-0')}
          >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <path d="M14 2v6h6" />
          </svg>
          {hasDetail && (
            <Chevron
              className={cn('absolute transition-[opacity,transform] duration-150 group-hover/row:opacity-100', open ? 'opacity-100' : 'opacity-0')}
              style={{ transform: open ? 'rotate(0deg)' : 'rotate(-90deg)' }}
            />
          )}
        </span>
        <span className="min-w-0 flex-1 truncate text-[12.5px] font-medium text-[var(--ink)]">{tool.label}</span>
        {tool.name && (
          <span className="inline-flex h-[22px] shrink-0 items-center rounded-[6px] bg-[var(--inset)] px-1.5 font-mono text-[11.5px] text-[var(--ink-2)] shadow-[0_0_0_1px_var(--line)] transition-colors duration-100 group-hover/row:bg-[var(--hover)]">
            {tool.name}
          </span>
        )}
      </button>
      {hasDetail && (
        <div
          className="bui-motion grid transition-[grid-template-rows,opacity] duration-300"
          style={{ gridTemplateRows: open ? '1fr' : '0fr', opacity: open ? 1 : 0, transitionTimingFunction: BUI_EASE }}
          aria-hidden={!open}
        >
          <div className="min-h-0 overflow-hidden">
            <div className="mt-0.5 mb-1 ml-2 flex flex-col gap-0.5 border-l border-[var(--line)] py-0.5 pl-3.5">
              {tool.detail!.map((line, i) => (
                <span key={`${i}-${line}`} className="truncate text-[11.5px] leading-[1.6] text-[var(--ink-2)]">
                  {line}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/** Collapsible run of tool calls: a "N tool calls" header over one row per tool, each optionally expandable. */
export function ToolChips({ tools }: { tools: ToolChip[] }) {
  const [open, setOpen] = useState(true)
  if (tools.length === 0) return null
  return (
    <div className="w-full max-w-80 pb-1">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="-mx-1.5 flex w-fit items-center gap-1.5 rounded-[8px] px-1.5 py-1 text-[12.5px] text-[var(--ink-2)] transition-colors duration-100 hover:bg-[var(--hover)] focus-visible:outline-2 focus-visible:outline-[var(--l3-violet)]"
      >
        <Chevron className="transition-transform duration-200" style={{ transform: open ? 'rotate(0deg)' : 'rotate(-90deg)' }} />
        <span className="tabular-nums">{tools.length === 1 ? '1 tool call' : `${tools.length} tool calls`}</span>
      </button>
      <div
        className="bui-motion grid transition-[grid-template-rows,opacity] duration-300"
        style={{ gridTemplateRows: open ? '1fr' : '0fr', opacity: open ? 1 : 0, transitionTimingFunction: BUI_EASE }}
        aria-hidden={!open}
      >
        {/* Collapsed rows leave the tab order. */}
        <div className="-mx-1 overflow-hidden px-1.5 pb-1" {...(open ? {} : INERT)}>
          <div className="mt-1.5 flex flex-col gap-1">
            {tools.map((tool, i) => (
              <ToolRow key={`${tool.name ?? tool.label}-${i}`} tool={tool} index={i} />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
